// Source : web/wp-content/themes/studiometa/src/js/components/StaggerReveal.js
// Enregistré dans app.js via : '[data-stagger-reveal]': StaggerReveal

import { Base } from '@studiometa/js-toolkit';
import { gsap } from '../utils/gsap.js';

/**
 * Révèle les enfants directs d'un conteneur, un par un (cascade).
 *
 * - Les enfants « texte » reprennent l'animation des titres (montée + léger flou
 *   + fondu, voir TitleReveal).
 * - Les enfants « image » (ceux qui contiennent un `<img>` ou un `<svg>`)
 *   n'ont qu'un simple fondu.
 *
 * Le déclenchement se fait à l'entrée dans le viewport (ScrollTrigger). En
 * `prefers-reduced-motion: reduce`, aucun mouvement : les enfants restent
 * affichés.
 * @augments {Base}
 */
export default class StaggerReveal extends Base {
  /**
   * @type {import('@studiometa/js-toolkit').BaseConfig}
   */
  static config = {
    name: 'StaggerReveal',
    options: {
      // Attente (en secondes) avant le démarrage de la cascade.
      delay: {
        type: Number,
        default: 0,
      },
      // Écart (en secondes) entre l'apparition de chaque enfant.
      stagger: {
        type: Number,
        default: 0.12,
      },
      // Durée (en secondes) de l'animation de chaque enfant.
      duration: {
        type: Number,
        default: 0.9,
      },
    },
  };

  /**
   * @type {import('gsap').MatchMedia | null}
   */
  mm = null;

  /**
   *
   */
  mounted() {
    const children = Array.from(this.$el.children);
    if (children.length === 0) {
      return;
    }

    // Un enfant est considéré « image » s'il est, ou contient, un `<img>`/`<svg>`.
    const isImage = (el) => el.matches('img, svg') || el.querySelector('img, svg') !== null;

    this.mm = gsap.matchMedia();

    this.mm.add(
      {
        motion: '(prefers-reduced-motion: no-preference)',
        reduced: '(prefers-reduced-motion: reduce)',
      },
      (context) => {
        if (context.conditions?.reduced) {
          gsap.set(children, { autoAlpha: 1 });
          return;
        }

        // État initial masqué (le flash est évité côté CSS, voir animate.css).
        gsap.set(children, { autoAlpha: 0 });

        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: this.$el,
            start: 'top 85%',
            once: true,
          },
        });

        const { delay, stagger, duration } = this.$options;

        // Cascade : chaque enfant entre `stagger`s après le précédent, dans l'ordre
        // du DOM, après un `delay` initial. Un enfant peut surcharger son moment
        // d'apparition avec l'attribut `data-reveal-delay="3.3"` (en secondes).
        // On utilise `fromTo` (et non `from`) : l'état final est explicite, sinon
        // `from` animerait vers la valeur courante — masquée par le `set` ci-dessus.
        children.forEach((child, index) => {
          const override = Number.parseFloat(child.dataset.revealDelay);
          const at = Number.isNaN(override) ? delay + index * stagger : override;

          // Type d'animation : `data-reveal-motion="slide|fade"` force le rendu
          // d'un enfant (ex. un SVG que l'on veut animer comme du texte). Sinon
          // auto : image => simple fondu, texte => montée + léger flou.
          const motion = child.dataset.revealMotion || (isImage(child) ? 'fade' : 'slide');

          if (motion === 'fade') {
            // Simple fondu.
            tl.fromTo(child, { autoAlpha: 0 }, { autoAlpha: 1, duration, ease: 'power3.out' }, at);
            return;
          }

          // Montée + léger flou + fondu (même animation que les titres).
          tl.fromTo(
            child,
            { autoAlpha: 0, yPercent: 110, filter: 'blur(8px)' },
            { autoAlpha: 1, yPercent: 0, filter: 'blur(0px)', duration, ease: 'power3.out' },
            at,
          );
        });
      },
    );
  }

  /**
   *
   */
  destroyed() {
    this.mm?.revert();
    this.mm = null;
  }
}
